import React, { useState, useEffect, useRef } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { isFloat } from 'common/Helpers'
import './Marquee.scss'

const marqueeKeyframes = (props) => keyframes`
    0% { right: 0 }
    ${props.animationStopPercentage}%, ${props.animationRollBackPercentage}% {right: ${props.differenceWidth}px}
    100% { right: 0 }
`
const marqueeAnimation = (props) => css`
    ${marqueeKeyframes} ${props.animationDuration}s linear 1s infinite forwards;
`

const MarqueeElement = styled.div`
    animation: ${marqueeAnimation};
    position: relative;
`

export function Marquee({ wrapClass = '', string, stringReference, holderReference, animationParams }) {
    let refString = useRef(0)
    let refHolder = useRef(0)

    refHolder = holderReference ? holderReference : refHolder
    refString = stringReference ? stringReference : refString

    const refStringOffsetWidth = refString.current?.offsetWidth
    const refHolderOffsetWidth = refHolder.current?.offsetWidth

    const [stringWidth, setStringWidth] = useState(0)
    const [isOverflow, setIsOverflow] = useState(false)
    const [differenceWidth, setNumberDifferenceWidth] = useState(0)

    const [animationDuration, setNumberAnimationDuration] = useState(0)
    const [animationStopPercentage, setNumberAnimationStopPercentage] = useState(0)
    const [animationRollBackPercentage, setNumberAnimationRollBackPercentage] = useState(0)

    useEffect(() => {
        refString.current?.offsetWidth && setStringWidth(refString.current.offsetWidth)
    }, [refStringOffsetWidth])

    useEffect(() => {
        const handleResize = () => {
            const animationSpeed = animationParams?.animationSpeed || 25
            const endFreezeTime = animationParams?.endFreezeTime || 1
            const rollBacktime = animationParams?.rollBacktime || 0.3

            const holderWidth = refHolder.current.offsetWidth

            setIsOverflow(stringWidth > holderWidth)
            setNumberDifferenceWidth(stringWidth - holderWidth)
            setNumberAnimationDuration((stringWidth - holderWidth) / animationSpeed + endFreezeTime + rollBacktime)

            // Calculate keyframe percentage where animation should end and start (n) secs freeze
            const numberEndFreezeTimePercent = (animationDuration - endFreezeTime) / (animationDuration / 100)
            setNumberAnimationStopPercentage(
                isFloat(numberEndFreezeTimePercent) ? numberEndFreezeTimePercent.toFixed(2) : numberEndFreezeTimePercent
            )

            // Calculate keyframe percentage when animation should go back to start point after (n) secs freeze
            const numberRollBackPercent = (animationDuration - rollBacktime) / (animationDuration / 100)
            setNumberAnimationRollBackPercentage(
                isFloat(numberRollBackPercent) ? numberRollBackPercent.toFixed(2) : numberRollBackPercent
            )
        }

        handleResize()
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [refHolderOffsetWidth, stringWidth, animationDuration, animationParams])

    const MarqueeHTML = (
        <>
            {isOverflow ? (
                <MarqueeElement
                    className={`marquee__element ${wrapClass}`}
                    differenceWidth={differenceWidth}
                    animationDuration={animationDuration}
                    animationStopPercentage={animationStopPercentage}
                    animationRollBackPercentage={animationRollBackPercentage}
                >
                    <span ref={refString}>{string}</span>
                </MarqueeElement>
            ) : (
                <div className={`marquee__element marquee__element--no-animation ${wrapClass}`}>
                    <span ref={refString}>{string}</span>
                </div>
            )}
        </>
    )

    return (
        <>
            {holderReference ? (
                MarqueeHTML
            ) : (
                <div ref={refHolder} className="marquee__holder">
                    {MarqueeHTML}
                </div>
            )}
        </>
    )
}
